import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Trash2Icon } from "lucide-react";
import { useGeneralFeatureFlags } from "@/contexts/general-feature-flags-context";

interface CreateCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseData?: {
    _id: Id<"courses">;
    title: string;
    courseCode?: string;
    professorName?: string;
    yearSemester?: string;
    additionalInfo?: string;
  };
}

export const CreateCourseModal = ({
  isOpen,
  onClose,
  courseData,
}: CreateCourseModalProps) => {
  const navigate = useNavigate();
  const { ensureEnabled } = useGeneralFeatureFlags();
  const [formData, setFormData] = useState({
    courseName: courseData?.title || "Untitled Course",
    courseCode: courseData?.courseCode || "",
    professorName: courseData?.professorName || "",
    yearSemester: courseData?.yearSemester || "",
    additionalInfo: courseData?.additionalInfo || "",
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        courseName: courseData?.title ?? (courseData ? "" : "Untitled Course"),
        courseCode: courseData?.courseCode ?? "",
        professorName: courseData?.professorName ?? "",
        yearSemester: courseData?.yearSemester ?? "",
        additionalInfo: courseData?.additionalInfo ?? "",
      });
    }
  }, [courseData, isOpen]);

  const [isArchiving, setIsArchiving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const createCourse = useMutation(api.courses.create);
  const updateCourse = useMutation(api.courses.update);
  const archiveCourse = useMutation(api.courses.archive);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.courseName.trim()) {
      toast.error("Please enter a course title");
      return;
    }

    if (!courseData && !ensureEnabled("course")) {
      return;
    }

    setIsArchiving(true);
    try {
      if (courseData) {
        await updateCourse({
          id: courseData._id,
          title: formData.courseName.trim(),
          courseCode: formData.courseCode,
          professorName: formData.professorName,
          yearSemester: formData.yearSemester,
          additionalInfo: formData.additionalInfo,
        });
        toast.success("Course updated successfully!");
      } else {
        const courseId = await createCourse({
          title: formData.courseName.trim(),
          courseCode: formData.courseCode,
          professorName: formData.professorName,
          yearSemester: formData.yearSemester,
          additionalInfo: formData.additionalInfo,
        });
        toast.success("Course created successfully");
        navigate(`/course/${courseId}`);
      }
      setFormData({
        courseName: "",
        courseCode: "",
        professorName: "",
        yearSemester: "",
        additionalInfo: "",
      });
      onClose();
    } catch (error) {
      console.error("Error saving course:", error);
      toast.error(courseData ? "Failed to update course" : "Failed to create course");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleArchive = async () => {
    if (!courseData) return;

    setIsArchiving(true);
    try {
      await archiveCourse({ id: courseData._id });
      toast.success("Course moved to trash");
      onClose();
      navigate("/");
    } catch (error) {
      console.error("Failed to move course to trash:", error);
      toast.error("Failed to move course to trash");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      courseName: "",
      courseCode: "",
      professorName: "",
      yearSemester: "",
      additionalInfo: "",
    });
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-h-[90vh] overflow-y-auto md:min-w-[80vw] lg:min-w-[60vw]">
          <DialogHeader>
            <DialogTitle className="font-serif italic">{courseData ? "Edit Course" : "Create New Course"}</DialogTitle>
            <DialogDescription>
              {courseData
                ? "Make changes to your course here. Click save when you're done."
                : "Add a new course and its details. Click create when you're done."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="courseName" className="block text-sm font-medium mb-1">
                Course Name *
              </label>
              <Input
                id="courseName"
                name="courseName"
                value={formData.courseName}
                onChange={handleChange}
                required
                placeholder="Enter course name"
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="w-full sm:w-1/2">
                <label htmlFor="courseCode" className="block text-sm font-medium mb-1">
                  Course Code
                </label>
                <Input
                  id="courseCode"
                  name="courseCode"
                  value={formData.courseCode}
                  onChange={handleChange}
                  placeholder="e.g., PHYS 1301"
                />
              </div>
              <div className="w-full sm:w-1/2">
                <label htmlFor="yearSemester" className="block text-sm font-medium mb-1">
                  Semester/Year
                </label>
                <Input
                  id="yearSemester"
                  name="yearSemester"
                  value={formData.yearSemester}
                  onChange={handleChange}
                  placeholder="e.g., Fall 2024"
                />
              </div>
            </div>

            <div>
              <label htmlFor="professorName" className="block text-sm font-medium mb-1">
                Professor Name
              </label>
              <Input
                id="professorName"
                name="professorName"
                value={formData.professorName}
                onChange={handleChange}
                placeholder="Enter professor's name"
              />
            </div>

            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-medium mb-1">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                className="w-full px-3 py-2 border bg-card shadow-xs rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px] text-sm"
                placeholder="Enter any additional information about the course. (e.g. Syllabus Links, Class Timings, Room Numbers, etc.)"
              />
            </div>

            <DialogFooter className="">
              <div className="flex justify-between w-full items-center">
                {courseData && (
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isArchiving}
                    className=""
                  >
                    <Trash2Icon className="w-4 h-4 " />
                    <p className="hidden md:block">{isArchiving ? "Moving to trash..." : "Move to Trash"}</p>
                  </Button>
                )}

                <div className="gap-1 flex justify-start ml-auto">
                  <Button
                    variant="ghost"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className=" text-purple-700 dark:text-purple-500 bg-purple-600/20 hover:bg-purple-600/30 "
                  >
                    {isArchiving ? "Saving..." : courseData ? "Save Changes" : "Create Course"}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif italic">Move to Trash</DialogTitle>
            <DialogDescription>
              Are you sure you want to move "{courseData?.title}" to trash? <strong>This will also move all associated notes and PDFs to trash.</strong> You can restore them within 7 days.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteDialog(false);
                handleArchive();
              }}
            >
              Move to Trash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}; 